import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { BmarkFwdRiskBlotterComponent } from "app/bfx-bmark-summary/blotters/spot-risk/bmark-fwd-risk-blotter.component";
import { SETTINGS_PROPERTY_AMOUNTVIEWTYPE } from "app/bfx-user-settings/bfx-bmark-summary-settings-preferences.component";
import { Subscription } from "rxjs";
import { ActiveUser, BmarkFixingData, BmarkFixingType } from "shared/cubiq-services";
import { BmarkClientOrdersBlotterMode } from "../../shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import { AmountViewType } from "../bfx-menus/bfx-blotter-settings-menu.component";
import { ChildBlotterComponent } from "./models/bmark-sub-blotter.interface";
import { BmarkFixingsManagerService } from "./services/bmark-fixings-manager.service";
import { BmarkSummaryRiskFilterType } from "app/bfx-bmark-summary/models/bmark-summary-risk-filter-type.enum";

const DEFAULT_AMOUNTVIEW_TYPE = AmountViewType.NOTIONAL;

/**
 * View component that contains the Forward Risk blotter in the BM Summary view
 */
@Component({
  selector: "bmark-summary-fwd-risk",
  templateUrl: "./html/bmark-summary-fwd-risk.html",
  standalone: false,
})
export class BmarkSummaryFwdRiskContainerComponent
  implements OnInit, ChildBlotterComponent, OnChanges, OnDestroy
{
  @Input() activeView: BmarkClientOrdersBlotterMode;

  @Input() riskFilter: BmarkSummaryRiskFilterType;

  /**********************************************************************/
  /* DATA */
  public orderIds: string = "";
  public tableView: string = "";
  public fixing: string = "";
  public type: string = "";

  private displayFixingSubscription: Subscription;

  @ViewChild(BmarkFwdRiskBlotterComponent, { static: true })
  bmarkFwdRiskBlotter: BmarkFwdRiskBlotterComponent;
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(private bmarkManager: BmarkFixingsManagerService) {}

  /**********************************************************************/
  /* INITIALIZE */
  ngOnInit() {
    this.initializeManager();
  }

  protected initializeManager(): void {
    this.displayFixingSubscription = this.bmarkManager.displayFixing$.subscribe(
      (fixingData: BmarkFixingData) => {
        if (fixingData instanceof BmarkFixingData) {
          this.type = `${BmarkFixingType[fixingData.type]}`;
          this.fixing = `${fixingData.displayTime} ${fixingData.code} (${fixingData.displayDate})`;
          this.orderIds = fixingData.hasClientOrders()
            ? fixingData.getClientOrderIds().toString()
            : "Unknown";
        } else {
          this.type = "";
          this.fixing = "";
          this.orderIds = "";
        }
      },
    );
  }

  /**********************************************************************/
  /* CHANGES */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      switch (propName) {
        case "activeView":
          this.tableView = BmarkClientOrdersBlotterMode[this.activeView];
          this.bmarkFwdRiskBlotter.setBmarkViewType(this.activeView);
          break;
      }
    }
  }
  /**********************************************************************/
  /* Destroy */
  ngOnDestroy() {
    if (this.displayFixingSubscription) {
      this.displayFixingSubscription.unsubscribe();
      this.displayFixingSubscription = null;
    }
  }

  setAmountViewType(type: AmountViewType) {
    this.bmarkFwdRiskBlotter.setAmountViewType(type);
  }

  getAmountViewType(): AmountViewType {
    return this.bmarkFwdRiskBlotter.getAmountViewType();
  }

  /**********************************************************************/
  /* BLOTTER - PROFILE */

  public getRiskConfiguration(): any {
    return this.bmarkFwdRiskBlotter.getComponentConfiguration();
  }

  public getBlotterId() {
    return this.bmarkFwdRiskBlotter.getBlotterId();
  }

  public getBlotterInstance() {
    return this.bmarkFwdRiskBlotter.getBlotterInstance();
  }

  filterSelectionChanged(filterConfiguration: FilterConfiguration) {
    // filter changes are handlled with events into the data service
  }

  getFilterConfiguration(): void {
    return null;
  }

  setBlotterFilterComponent(blotterFilter: any): void {
    // Spot risk does not support blotter filtering - only through service events
  }

  refreshSelectionCells(): void {
    this.bmarkFwdRiskBlotter.refreshSelectionCells();
  }
  hasCellRenderedSelection(): boolean {
    return this.bmarkFwdRiskBlotter.hasCellRenderedSelection();
  }

  public getProfileSettingKey(): string {
    return this.bmarkFwdRiskBlotter.getProfileSettingKey();
  }

  public initializeAmountView(userData: ActiveUser): AmountViewType {
    let ret: AmountViewType = DEFAULT_AMOUNTVIEW_TYPE;
    let jsonString: string = userData.profileSettings.get(this.getProfileSettingKey());
    let spotRiskSettings = jsonString != null ? JSON.parse(jsonString) : null;
    if (spotRiskSettings && spotRiskSettings.hasOwnProperty(SETTINGS_PROPERTY_AMOUNTVIEWTYPE)) {
      ret = spotRiskSettings[SETTINGS_PROPERTY_AMOUNTVIEWTYPE];
      if (null == ret) {
        ret = DEFAULT_AMOUNTVIEW_TYPE;
      }

      this.setAmountViewType(ret);
    }

    return ret;
  }
}
